import posthog from "posthog-js";

let _userId: string | null = null;

function sendToApi(endpoint: string, payload: Record<string, unknown>) {
  try {
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never let analytics break the app
  }
}

/**
 * Set the current user ID for analytics tracking.
 * Called from providers.tsx when auth state changes.
 */
export function setAnalyticsUser(userId: string | null) {
  _userId = userId;
}

type TrackOptions = {
  /**
   * Ueberschreibt die `user_id` fuer genau dieses Ereignis.
   *
   * Noetig, wo das Ereignis in derselben Runde feuert, in der die Anmeldung
   * geschieht: `_userId` wird von `setAnalyticsUser` aus einem Effect in
   * providers.tsx gesetzt, der erst im naechsten Render laeuft. `user_logged_in`
   * und `membership_signup_completed` waren deshalb in Production ausnahmslos
   * ohne `user_id` — 720 bzw. 338 Ereignisse.
   *
   * Ueberall sonst ungenutzt lassen: `_userId` ist dort bereits korrekt, und
   * eine manuelle ID koennte davon abweichen.
   */
  userId?: string | null;
};

/**
 * Track an event — sends to both PostHog and our DB.
 * Drop-in replacement for posthog.capture().
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
  options?: TrackOptions
) {
  // Send to PostHog (existing behavior). Ausserhalb der Produktion ist
  // PostHog nicht initialisiert — der Versand an die eigene DB unten laeuft
  // aber weiter, damit Staging-Tests in analytics_events sichtbar bleiben.
  //
  // `undefined` heisst "nicht uebergeben" und faellt auf `_userId` zurueck;
  // ein ausdrueckliches `null` bleibt `null`, statt still ueberschrieben zu
  // werden. Einmal aufgeloest, damit beide Senken unten denselben Wert sehen.
  const resolvedUserId =
    options && "userId" in options ? options.userId : _userId;

  // PostHog hat dieselbe Luecke wie die eigene DB: `posthog.identify` laeuft
  // ebenfalls erst im Effect in providers.tsx, das Ereignis haengt also noch
  // am anonymen Distinct-ID. Ein `identify` hier waere der sauberere Weg,
  // greift aber in PostHogs Identitaetsverwaltung ein — stattdessen reist die
  // ID als Property mit, womit sich das Ereignis dort nachtraeglich zuordnen
  // laesst.
  if (posthog.__loaded) {
    posthog.capture(
      eventName,
      resolvedUserId ? { ...properties, user_id: resolvedUserId } : properties,
    );
  }

  // Send to our DB (fire-and-forget)
  sendToApi("/api/analytics/track", {
    eventName,
    properties: properties ?? null,
    url: typeof window !== "undefined" ? window.location.href.split("#")[0] : null,
    userId: resolvedUserId,
  });
}

/**
 * Log a client-side error to our DB (fire-and-forget).
 */
export function logError(
  error: unknown,
  metadata?: Record<string, unknown>
) {
  const message =
    error instanceof Error ? error.message : String(error);
  const stack =
    error instanceof Error ? error.stack ?? null : null;

  sendToApi("/api/analytics/error", {
    message,
    stack,
    url: typeof window !== "undefined" ? window.location.href : null,
    source: "client",
    userId: _userId,
    metadata: metadata ?? null,
  });
}

/**
 * Log a server-side error directly to the DB.
 * Use in API route catch blocks.
 */
export async function logServerError(
  error: unknown,
  context?: { userId?: string; url?: string; metadata?: Record<string, unknown> }
) {
  try {
    const { db } = await import("@/lib/db");
    const { errorLogs } = await import("@/lib/db/schema");

    await db.insert(errorLogs).values({
      userId: context?.userId ?? null,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? (error.stack ?? null) : null,
      url: context?.url ?? null,
      source: "server" as const,
      metadata: context?.metadata ?? null,
    });
  } catch (err) {
    console.error("[logServerError] Failed to log:", err);
  }
}
